import { ApplicationConfig, Injectable, provideBrowserGlobalErrorListeners, LOCALE_ID, importProvidersFrom, inject } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors, HttpClient } from '@angular/common/http';

import { routes } from './app.routes';
import { solutionTokenInterceptor } from './core/services/solution-token.service';
import { sessionRestoreInterceptor } from './core/interceptors/session-restore.interceptor';

import { MAT_DATE_LOCALE, MAT_DATE_FORMATS, provideNativeDateAdapter } from '@angular/material/core';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MatPaginatorIntl } from '@angular/material/paginator';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

/**
 * The paginator's labels, in the language the app is showing.
 *
 * These used to be fixed Spanish strings, so every table read "Elementos por página:" and
 * "1-20 de 45" whatever the language. Material only re-reads the labels when `changes`
 * emits, so it is pinged whenever the language switches or its translations finish loading
 * — the first language is applied before its file has arrived.
 */
@Injectable()
export class CustomPaginatorIntl extends MatPaginatorIntl {
  private readonly translate = inject(TranslateService);

  override itemsPerPageLabel = 'Elementos por página:';
  override nextPageLabel = '';
  override previousPageLabel = '';
  override firstPageLabel = '';
  override lastPageLabel = '';

  constructor() {
    super();
    // Lives as long as the app: the intl is a root singleton.
    this.translate.stream('Elementos por página:').subscribe((label: string) => {
      this.itemsPerPageLabel = label;
      this.changes.next();
    });
  }

  override getRangeLabel = (page: number, pageSize: number, length: number) => {
    if (length === 0 || pageSize === 0) {
      return this.translate.instant('0 de {{length}}', { length });
    }
    length = Math.max(length, 0);
    const startIndex = page * pageSize;
    const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;
    return this.translate.instant('{{start}}-{{end}} de {{length}}', {
      start: startIndex + 1,
      end: endIndex,
      length,
    });
  };
}

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: { month: 'short', year: 'numeric', day: 'numeric' },
    timeInput: null,
  },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
    monthYearLabel: { year: 'numeric', month: 'long' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
    timeInput: { hour: 'numeric', minute: 'numeric' },
    timeOptionLabel: { hour: 'numeric', minute: 'numeric' },
  }
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(withFetch(), withInterceptors([sessionRestoreInterceptor, solutionTokenInterceptor])),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'es',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      })
    ),
    { provide: MAT_DATE_LOCALE, useExisting: LOCALE_ID },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MatPaginatorIntl, useClass: CustomPaginatorIntl },
    provideNativeDateAdapter()
  ]
};
